<template>
  <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" :initial-values="initialFormValues" as="div">
    <form @submit="handleSubmit($event, onContinue)">
      <div class="grid grid-cols-2 gap-x-4 gap-y-3">
        <div class="flex-1 mt-1">
          <label for="referenceId" class="flex items-center text-sm text-gray-700">
            {{ t("contract_reference") }}
            <span class="ml-1 text-red-600">*</span>
          </label>
          <div class="mt-1">
            <Field
              id="referenceId"
              name="product.referenceId"
              type="text"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="BOX-REF-123"
              v-model="localProduct.referenceId"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="product.referenceId" class="mt-2 text-sm text-red-600" />
          </div>
        </div>

        <div class="flex-1 mt-1">
          <label for="lineHolderName" class="flex items-center text-sm text-gray-700">
            {{ t("line_holder") }}
            <span class="ml-1 text-red-600">*</span>
          </label>
          <div class="mt-1">
            <Field
              id="lineHolderName"
              name="product.lineHolderName"
              type="text"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              :placeholder="t('line_holder_placeholder')"
              v-model="localProduct.lineHolderName"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="product.lineHolderName" class="mt-2 text-sm text-red-600" />
          </div>
        </div>

        <div class="flex-1 mt-1">
          <label for="transactionDate" class="flex items-center text-sm text-gray-700">
            {{ t("subscription_date") }}
            <span class="ml-1 text-red-600">*</span>
          </label>
          <div class="mt-1">
            <Field
              id="transactionDate"
              name="product.transactionDate"
              type="date"
              :max="today"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              v-model="localProduct.transactionDate"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="product.transactionDate" class="mt-2 text-sm text-red-600" />
          </div>
        </div>
      </div>

      <div class="mt-4 flex justify-end">
        <SecondaryButton type="submit">{{ t("continue_button") }}</SecondaryButton>
      </div>
    </form>
  </VeeForm>
</template>

<script>
import * as yup from "yup";
import { useI18n } from "vue-i18n";
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import { SecondaryButton } from "@estaly/ui";
import { blockEnterBehavior } from "@/utils/blockEnterBehavior";

export default {
  components: {
    VeeForm,
    Field,
    ErrorMessage,
    SecondaryButton,
  },
  props: {
    modelValue: Object,
  },
  emits: ["update:modelValue", "continue"],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  computed: {
    today() {
      return new Date().toISOString().split("T")[0];
    },

    minDate() {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date.toISOString().split("T")[0];
    },

    schema() {
      const t = this.t;
      return yup.object({
        product: yup.object({
          referenceId: yup
            .string()
            .required(() => t("validation_reference_required"))
            .min(4, () => t("validation_reference_min")),
          lineHolderName: yup.string().required(() => t("validation_line_holder_required")),
          transactionDate: yup
            .string()
            .required(() => t("validation_subscription_date_required"))
            .test(
              "not-future",
              () => t("validation_subscription_date_not_future"),
              (value) => {
                if (!value) return true;
                return value <= this.today;
              },
            )
            .test(
              "not-too-old",
              () => t("validation_subscription_date_not_too_old"),
              (value) => {
                if (!value) return true;
                return value >= this.minDate;
              },
            ),
        }),
      });
    },

    initialFormValues() {
      return {
        product: this.modelValue || { referenceId: "", lineHolderName: "", transactionDate: "" },
      };
    },

    localProduct: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    "localProduct.lineHolderName": {
      handler(name) {
        this.localProduct = {
          ...this.localProduct,
          title: name ? `Offre internet et/ou TV - ${name}` : "",
        };
      },
    },
  },
  methods: {
    onContinue() {
      this.$emit("continue");
    },

    blockEnterBehavior,
  },
};
</script>

<i18n src="./VirtualProductForm.i18n.json"></i18n>
